
export interface PricingPlan {
  id: string;
  name: string;
  price: string;
  slug: string;
  period: string;
  description: string;
  yearlyPrice?: string;
  priceMonthly?: number;
  priceYearly?: number;
  monthlyPriceDisplay?: string;
  monthlyPeriodDisplay?: string;
  yearlyPriceDisplay?: string;
  yearlyPeriodDisplay?: string;
  yearlySavingsDisplay?: string | null;
  trialInfo?: string;
  trialCredits?: number;
  creditsPerDollar?: number;
  monthlyCredits?: number;
  yearlyBonusPercent?: number;
  monthlyBonusPercent?: number;
  topupDiscountPercent?: number;
  tierType?: string;
  features: string[];
  cta: string;
  popular: boolean;
  badge?: string;
  disabled: boolean;
  max_users?: number;
  max_candidates?: number;
}

export interface PricingApiResponse {
  success: boolean;
  plans: PricingPlanApi[];
}

export type BillingPeriod = 'monthly' | 'yearly';

export interface PricingPeriodApi {
  price: string;
  period_display: string;
  billing_period: 'MONTHLY' | 'YEARLY' | string;
  yearly_savings_display?: string | null;
}

export interface CreateCheckoutSessionPayload {
  tier_id: string;
  billing_period: string;
  company_id: string;
  success_url: string;
  cancel_url: string;
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface CurrentPlan {
  company_id: string;
  subscription_id: string;
  subscription_status: string;
  subscription_status_msg: string;
  billing_period: string;
  plan: string;
  name: string;
  description: string;
  locked_price: number;
  currency: string;
  auto_renewal: boolean;
  current_period_start: string;
  current_period_end: string;
  payment_method: string;
  credits_per_dollar: number;
  features: string[];
  is_active: boolean;
  is_public: boolean;
  feature_flags: Record<string, boolean>;
  show_current_plan: boolean;
}

export interface PricingPlanApi {
  id: string;
  name: string;
  slug?: string;
  price?: string;
  period?: string;
  description: string;
  month?: PricingPeriodApi;
  year?: PricingPeriodApi;
  yearlyPrice?: string;
  price_monthly?: number;
  price_yearly?: number;
  priceMonthly?: number;
  priceYearly?: number;
  trial_info?: string;
  'trial credits'?: number;
  trial_credits?: number;
  credits_per_dollar?: number;
  monthly_credits?: number;
  yearly_bonus_percent?: number;
  monthly_bonus_percent?: number;
  topup_discount_percent?: number;
  features: string[];
  cta: string;
  popular?: boolean;
  badge?: string;
  disabled?: boolean;
  max_users?: number;
  max_candidates?: number;
  tier_type: string
}

const parsePrice = (price?: string) => {
  if (!price) {
    return undefined;
  }

  const match = price.match(/\$([\d,]+(?:\.\d+)?)/);
  return match ? Number(match[1].replace(/,/g, '')) : undefined;
};

// Helper function to transform API model to domain model
export const transformPricingPlan = (apiPlan: PricingPlanApi): PricingPlan => ({
  id: apiPlan.id,
  name: apiPlan.name,
  price: apiPlan.month?.price ?? apiPlan.price ?? '',
  period: apiPlan.month?.period_display ?? (apiPlan.period?.startsWith('/') ? apiPlan.period : `/${apiPlan.period || 'month'}`),
  description: apiPlan.description,
  yearlyPrice: apiPlan.year?.price ?? apiPlan.yearlyPrice,
  priceMonthly: apiPlan.priceMonthly ?? apiPlan.price_monthly ?? parsePrice(apiPlan.month?.price ?? apiPlan.price),
  priceYearly: apiPlan.priceYearly ?? apiPlan.price_yearly ?? parsePrice(apiPlan.year?.price ?? apiPlan.yearlyPrice),
  monthlyPriceDisplay: apiPlan.month?.price ?? apiPlan.price,
  monthlyPeriodDisplay: apiPlan.month?.period_display,
  yearlyPriceDisplay: apiPlan.year?.price ?? apiPlan.yearlyPrice,
  yearlyPeriodDisplay: apiPlan.year?.period_display,
  yearlySavingsDisplay: apiPlan.year?.yearly_savings_display,
  trialInfo: apiPlan.trial_info,
  trialCredits: apiPlan.trial_credits ?? apiPlan['trial credits'],
  creditsPerDollar: apiPlan.credits_per_dollar,
  monthlyCredits: apiPlan.monthly_credits,
  yearlyBonusPercent: apiPlan.yearly_bonus_percent,
  monthlyBonusPercent: apiPlan.monthly_bonus_percent,
  topupDiscountPercent: apiPlan.topup_discount_percent,
  tierType: apiPlan.tier_type,
  features: Array.isArray(apiPlan.features) ? apiPlan.features : [],
  cta: apiPlan.cta || 'Get Started',
  popular: apiPlan.popular || apiPlan.name === 'Pro Plan' || apiPlan.name === 'Pro',
  badge: apiPlan.badge,
  disabled: Boolean(apiPlan.disabled),
  max_users: apiPlan.max_users,
  max_candidates: apiPlan.max_candidates,
  slug: apiPlan.slug ?? apiPlan.name.toLowerCase().replace(/\s+/g, '-'),
});

